import React, { useState, useEffect, useRef } from 'react';
import { X, Send, User, Store, Package, Clock, MessageCircle } from 'lucide-react';
import { getMessages, sendMessage } from '../../services/api';
import { getImageUrl } from '../../utils/imageUtils';

const ChatModal = ({ isOpen, onClose, conversation, listing, seller, currentUser }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (isOpen && conversation) {
      fetchMessages();
    }
  }, [isOpen, conversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchMessages = async () => {
    if (!conversation) return;
    
    setLoading(true);
    try {
      const response = await getMessages(conversation.conversation_id);
      setMessages(response.data || []);
    } catch (error) {
      // console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async () => {
    if (!input.trim() || !conversation || sending) return;

    setSending(true);
    const tempId = Date.now();
    const tempMessage = {
      message_id: tempId,
      sender_id: currentUser.user_id,
      message_text: input.trim(),
      created_at: new Date().toISOString(),
      isTemp: true,
    };

    setMessages(prev => [...prev, tempMessage]);
    setInput('');

    try {
      const response = await sendMessage({
        conversation_id: conversation.conversation_id,
        sender_id: currentUser.user_id,
        message_text: input.trim(),
      });

      // Replace temp message with real one
      setMessages(prev => prev.map(msg => 
        msg.isTemp && msg.message_id === tempId 
          ? { ...response.data, isTemp: false } 
          : msg
      ));
    } catch (error) {
      // console.error('Error sending message:', error);
      // Remove temp message on error
      setMessages(prev => prev.filter(msg => !msg.isTemp || msg.message_id !== tempId));
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Overlay */}
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        {/* Modal */}
        <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          {/* Header */}
          <div className="bg-white px-6 py-4 border-b flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {seller.profile_picture_url ? (
                <img
                  src={getImageUrl(seller.profile_picture_url)}
                  alt={seller.store_name || `${seller.first_name} ${seller.last_name}`}
                  className="w-10 h-10 rounded-full"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center text-white font-bold">
                  {seller.first_name?.[0] || 'S'}
                </div>
              )}
              <div>
                <h3 className="font-bold text-gray-900">
                  {seller.store_name || `${seller.first_name} ${seller.last_name}`}
                </h3>
                <div className="flex items-center text-sm text-gray-500">
                  <Package className="w-3 h-3 mr-1" />
                  <span className="truncate max-w-[200px]">{listing.title}</span>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Messages Container */}
          <div className="h-96 overflow-y-auto p-4 bg-gray-50">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-gray-500">Loading messages...</div>
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-500">
                <MessageCircle className="w-12 h-12 mb-4 text-gray-300" />
                <p>No messages yet</p>
                <p className="text-sm mt-2">Start the conversation</p>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((message) => {
                  const isMyMessage = message.sender_id === currentUser.user_id;
                  return (
                    <div
                      key={message.message_id || message.tempId}
                      className={`flex ${isMyMessage ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-xs lg:max-w-md rounded-2xl px-4 py-2 ${
                          isMyMessage
                            ? 'bg-orange-500 text-white rounded-tr-none'
                            : 'bg-white text-gray-900 border border-gray-200 rounded-tl-none'
                        } ${message.isTemp ? 'opacity-70' : ''}`}
                      >
                        <p className="break-words">{message.message_text}</p>
                        <div className={`text-xs mt-1 flex justify-end ${isMyMessage ? 'text-orange-100' : 'text-gray-500'}`}>
                          {formatTime(message.created_at)}
                          {message.isTemp && ' ⏳'}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Input Area */}
          <div className="border-t p-4">
            <div className="flex space-x-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                placeholder="Type your message..."
                className="flex-1 border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                disabled={sending}
              />
              <button
                onClick={handleSend}
                disabled={!input.trim() || sending}
                className="bg-orange-500 text-white p-3 rounded-xl hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {sending ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatModal;